import { useState } from "react";

import {
  useFetchAuthenticate,
  useFetchNfe,
  useAPIAuthenticateToast,
  useAPINFE2Toast,
  useAPINFE2,
  useNfeAPIAuthenticate,
  useToast,
} from "hooks";
import { useAPIToast } from "hooks/useAPIToast";

import { cleanObject } from "utils";
import { getURLWithParams } from "utils/context";

export const useReports = (params = {}) =>
  useFetchAuthenticate(getURLWithParams("/reports", params));

export const useReportsCfopCsts = (params = {}) =>
  useFetchNfe(getURLWithParams("/reports/cfops-csts", params));

export const useReportsCfops = (params = {}) =>
  useFetchNfe(getURLWithParams("/reports/cfops", params));

export const useReportsCfopsDash = (params = {}) =>
  useFetchNfe(getURLWithParams("/reports/cfops/dash", params));

export const useReportsTotalsModelList = (params = {}) =>
  useFetchNfe(getURLWithParams("/cfops/cfops-empresas", params));

export const useReportsCfopsForm = () => {
  const { isLoading, onSend } = useAPINFE2Toast();

  const onEditCfop = data =>
    onSend({
      endpoint: "/cfops/cfops-empresas",
      messageSuccess: "CFOP atualizado com sucesso!",
      messageFail: "Falha ao tentar atualizar CFOP!",
      data,
      type: "PUT",
    });

  return {
    onEditCfop,
    isLoading,
  };
};

export const useReportsSkus = (params = {}) =>
  useFetchNfe(getURLWithParams("/reports/products-abc", params), undefined, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    keepPreviousData: true,
    revalidateIfStale: false,
  });

export const useProductsAbcCurva = (params = {}) =>
  useFetchNfe(getURLWithParams("/reports/curva-abc", params));

export const useProductsAbcTotais = (params = {}) =>
  useFetchNfe(getURLWithParams("/reports/totais-curva-abc", params));

export const useProductsAbcMovimentos = (params = {}) =>
  useFetchNfe(params?.cprod ? getURLWithParams("/reports/curva-abc-movimentos", params) : null);

export const useDownloadSkus = (clicked, params = {}) =>
  useFetchNfe(
    clicked ? getURLWithParams("/reports/skus/download", params) : null,
  );

export const useReportsDocumentosFiscais = (params = {}) =>
  useFetchNfe(getURLWithParams("/reports/documentos-fiscais", params));

export const useReportsPreviewPisAndIcms = params =>
  useFetchNfe(
    params && getURLWithParams("/tax-note/preview/pis-and-icms", params),
  );

export const useTotalSumPreviewPisAndIcms = params =>
  useFetchNfe(
    params && getURLWithParams("/tax-note/sum-preview/pis-and-icms", params),
  );

const useDownloadGenerate = ({ endpoint, fileName }) => {
  const api = useAPINFE2();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const onCSVGenerate = async params => {
    try {
      setIsLoading(true);
      const { data } = await api({
        method: "GET",
        url: getURLWithParams(endpoint, params),
      });
      const link = document.createElement("a");
      link.href = data;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      addToast({
        type: "error",
        title: "FALHA",
        description: "Falha ao tentar gerar o arquivo!",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return { onCSVGenerate, isLoading };
};

export const useDownloadPrevPisIcms = () =>
  useDownloadGenerate({
    endpoint: "/tax-note/preview/pis-and-icms/download",
    fileName: "pis-icms.csv",
  });

export const useDownloadPrevPisIcmsXlsx = () =>
  useDownloadGenerate({
    endpoint: "/tax-note/preview/pis-and-icms/download-xlsx",
    fileName: "pis-icms.xlsx",
  });

export const useReportsDocumentosFiscaisXMLActions = () => {
  const { isLoading, onSend } = useAPIAuthenticateToast();

  const onCSVGenerate = data =>
    onSend({
      endpoint: "/action/gerar-csv-xmls",
      messageSuccess: "Arquivo esta sendo processado favor verificar o email!",
      messageFail: "Falha ao tentar gerar CSV!",
      data,
    });

  return {
    onCSVGenerate,
    isLoading,
  };
};

export const useIgnoreProduct = () => {
  const api = useNfeAPIAuthenticate();
  const { onSend, isLoading, data } = useAPIToast(api);

  const onIgnoreProduct = data =>
    onSend({
      endpoint: "/tax-note/product/ignore",
      messageSuccess: "Produto ignorado com sucesso",
      messageFail: "Falha ao tentar ignorar produto",
      type: "POST",
      data,
    });

  const onUnignoreProduct = id =>
    onSend({
      endpoint: `/tax-note/products/ignored/${id}`,
      messageSuccess: "Produto removido com sucesso",
      messageFail: "Falha ao tentar remover produto",
      type: "DELETE",
    });

  return {
    onIgnoreProduct,
    onUnignoreProduct,
    data,
    isLoading,
  };
};

export const useIgnoreAllProductsFromNote = () => {
  const api = useNfeAPIAuthenticate();
  const { onSend, isLoading, data } = useAPIToast(api);

  const onIgnoreAllProductsFromNote = data =>
    onSend({
      endpoint: "/tax-note/all-products-from-note/ignore",
      messageSuccess: "Todos os produtos dessa nota ignorados com sucesso!",
      messageFail: "Falha ao tentar ignorar produtos da nota",
      type: "POST",
      data,
    });

  return {
    onIgnoreAllProductsFromNote,
    data,
    isLoading,
  };
};

export const useIgnoreNote = () => {
  const api = useNfeAPIAuthenticate();
  const { onSend, isLoading, data } = useAPIToast(api);

  const onIgnoreNote = data =>
    onSend({
      endpoint: "/tax-note/ignore",
      messageSuccess: "Nota fiscal ignorada com sucesso!",
      messageFail: "Falha ao tentar ignorar nota fiscal",
      type: "POST",
      data,
    });

  const onUnignoreNote = id =>
    onSend({
      endpoint: `/tax-note/ignored/${id}`,
      messageSuccess: "Nota fiscal removida com sucesso",
      messageFail: "Falha ao tentar remover nota fiscal",
      type: "DELETE",
    });

  return {
    onIgnoreNote,
    onUnignoreNote,
    data,
    isLoading,
  };
};

export const useIcmsNcmResumo = params =>
  useFetchNfe(
    params?.issue_date_start
      ? getURLWithParams("/reports/icms-produtos/resumo", cleanObject(params))
      : null,
  );

export const useIcmsNcmItens = params =>
  useFetchNfe(
    params?.issue_date_start && params?.ncm
      ? getURLWithParams("/reports/icms-produtos/itens", cleanObject(params))
      : null,
  );

export const useIcmsNcmNotas = params =>
  useFetchNfe(
    params?.issue_date_start && params?.cprod
      ? getURLWithParams("/reports/icms-produtos/notas", cleanObject(params))
      : null,
  );
